import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { WorkspaceProvider } from "@/lib/workspace";
import { preloadCommodities } from "@/lib/commodities";
import { preloadLithologies } from "@/lib/lithologies";
import { preloadCurrencies } from "@/lib/currencies";
import { preloadUnitSystems } from "@/lib/units";
// Eager routes — landing + login must be instant.
import LandingPage from "@/pages/LandingPage";
import FeaturesPage from "@/pages/FeaturesPage";
import PricingPage from "@/pages/PricingPage";
import ResourcesPage from "@/pages/ResourcesPage";
import AboutPage from "@/pages/AboutPage";
import BackToTop from "@/components/BackToTop";
import LoginPage from "@/pages/LoginPage";
// Static marketing surface — lazy-loaded so the initial bundle stays lean.
const ContactPage             = lazy(() => import("@/pages/ContactPage"));
const CareersPage             = lazy(() => import("@/pages/CareersPage"));
const SupportPage             = lazy(() => import("@/pages/SupportPage"));
const PrivacyPage             = lazy(() => import("@/pages/PrivacyPage"));
const FAQPage                 = lazy(() => import("@/pages/FAQPage"));
const ModulesPage             = lazy(() => import("@/pages/ModulesPage"));
const WorkflowPage            = lazy(() => import("@/pages/WorkflowPage"));
const UseCasesPage            = lazy(() => import("@/pages/UseCasesPage"));
const TutorialsPage           = lazy(() => import("@/pages/TutorialsPage"));
const BlogPage                = lazy(() => import("@/pages/BlogPage"));
const CaseStudiesPage         = lazy(() => import("@/pages/CaseStudiesPage"));
const DocumentationPage       = lazy(() => import("@/pages/DocumentationPage"));
const SystemRequirementsPage  = lazy(() => import("@/pages/SystemRequirementsPage"));
const IntegrationsPage        = lazy(() => import("@/pages/IntegrationsPage"));
const AIAssistantPage         = lazy(() => import("@/pages/AIAssistantPage"));
const ReportingPage           = lazy(() => import("@/pages/ReportingPage"));
const SecurityPage            = lazy(() => import("@/pages/SecurityPage"));
const ProductPage             = lazy(() => import("@/pages/ProductPage"));
const HowItWorksPage          = lazy(() => import("@/pages/HowItWorksPage"));
const DrillholeImportPage     = lazy(() => import("@/pages/DrillholeImportPage"));
// Everything else lazy-loaded to shrink the initial bundle by ~60%
// (V4.0 Sprint 4 Perf Audit — Route code-splitting).
const DashboardPage           = lazy(() => import("@/pages/DashboardPage"));
const ProjectsPage            = lazy(() => import("@/pages/ProjectsPage"));
const ProjectDetailPage       = lazy(() => import("@/pages/ProjectDetailPage"));
const ProjectMasterPage       = lazy(() => import("@/pages/ProjectMasterPage"));
const CompanyKnowledgeBasePage = lazy(() => import("@/pages/CompanyKnowledgeBasePage"));
const MyReportsPage           = lazy(() => import("@/pages/MyReportsPage"));
const ProViewerPage           = lazy(() => import("@/pages/ProViewerPage"));
const SectionViewer2DPage     = lazy(() => import("@/pages/SectionViewer2DPage"));
const EstimationWizardPage    = lazy(() => import("@/pages/EstimationWizardPage"));
const GeostatisticsPage       = lazy(() => import("@/pages/geostatistics/GeostatisticsPage"));
const EnterprisePage          = lazy(() => import("@/pages/enterprise/EnterprisePage"));
const EnterpriseWorkspacePage = lazy(() => import("@/pages/workspace/EnterpriseWorkspacePage"));
const DetachedViewportPage    = lazy(() => import("@/pages/workspace/DetachedViewportPage"));
const VerifyReportPage        = lazy(() => import("@/pages/VerifyReportPage"));
const ProjectComparisonPage    = lazy(() => import("@/pages/ProjectComparisonPage"));
const ProjectViewerPage       = lazy(() => import("@/pages/ProjectViewerPage"));
const PublicProjectPage       = lazy(() => import("@/pages/PublicProjectPage"));
const PublicResourceStatementPage = lazy(() => import("@/pages/PublicResourceStatementPage"));
const CompanySettingsPage     = lazy(() => import("@/pages/CompanySettingsPage"));
const CompanyBrandingPage     = lazy(() => import("@/pages/CompanyBrandingPage"));
const ReportTemplatesPage     = lazy(() => import("@/pages/ReportTemplatesPage"));
const BillingPage             = lazy(() => import("@/pages/BillingPage"));
const ProfilePage             = lazy(() => import("@/pages/ProfilePage"));
const AdminUsersPage          = lazy(() => import("@/pages/AdminUsersPage"));
const PaymentSuccessPage      = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelPage       = lazy(() => import("@/pages/PaymentCancelPage"));
const SignupPage              = lazy(() => import("@/pages/SignupPage"));
const SSOCallbackPage         = lazy(() => import("@/pages/SSOCallbackPage"));
import ShellSwitcher from "@/components/ShellSwitcher";
import IdleTimeoutModal from "@/components/IdleTimeoutModal";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import "@/styles/workspace-shell.css";
import "@/styles/responsive.css";

// Skeleton shown while a lazy route chunk is downloading.
function RouteSkeleton() {
  return (
    <div className="min-h-screen grid place-items-center text-muted-foreground font-mono">
      <div className="flex items-center gap-3">
        <div className="h-3 w-3 rounded-full bg-primary animate-pulse" />
        <span className="text-[11px] uppercase tracking-widest">Loading module…</span>
      </div>
    </div>
  );
}

// Warm the commodity + lithology catalogs once at boot so every component
// below can call getCommodity(code) / getLithologyCategories() synchronously.
preloadCommodities();
preloadLithologies();
preloadCurrencies();
preloadUnitSystems();

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground font-mono">Booting SMIN…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing surface */}
      <Route path="/" element={<LandingPage />} />
      {/* Legacy /landing bookmarks redirect to the new canonical root URL. */}
      <Route path="/landing" element={<Navigate to="/" replace />} />
      <Route path="/features"  element={<FeaturesPage />} />
      <Route path="/pricing"   element={<PricingPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/about"     element={<AboutPage />} />
      {/* New static marketing pages (Feb 2026 content wiring) */}
      <Route path="/contact"             element={<ContactPage />} />
      <Route path="/careers"             element={<CareersPage />} />
      <Route path="/support"             element={<SupportPage />} />
      <Route path="/privacy"             element={<PrivacyPage />} />
      <Route path="/faq"                 element={<FAQPage />} />
      <Route path="/modules"             element={<ModulesPage />} />
      <Route path="/workflow"            element={<WorkflowPage />} />
      <Route path="/use-cases"           element={<UseCasesPage />} />
      <Route path="/tutorials"           element={<TutorialsPage />} />
      <Route path="/blog"                element={<BlogPage />} />
      <Route path="/case-studies"        element={<CaseStudiesPage />} />
      <Route path="/documentation"       element={<DocumentationPage />} />
      <Route path="/system-requirements" element={<SystemRequirementsPage />} />
      <Route path="/integrations"        element={<IntegrationsPage />} />
      <Route path="/ai-assistant"        element={<AIAssistantPage />} />
      <Route path="/reporting"           element={<ReportingPage />} />
      <Route path="/security"            element={<SecurityPage />} />
      <Route path="/product"             element={<ProductPage />} />
      <Route path="/how-it-works"        element={<HowItWorksPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<SignupPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/sso/complete" element={<SSOCallbackPage />} />
      {/* 2FA routes removed in Iter 17 — redirect legacy bookmarks to /login */}
      <Route path="/2fa/enroll" element={<Navigate to="/login" replace />} />
      <Route path="/2fa/challenge" element={<Navigate to="/login" replace />} />
      <Route path="/public/:token" element={<PublicProjectPage />} />
      <Route path="/p/:token" element={<PublicResourceStatementPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      {/* Public "Request Access" surface — retired Feb 2026 when self-serve
          signup returned. Any legacy bookmarks redirect to the new /signup. */}
      <Route path="/request-access" element={<Navigate to="/signup" replace />} />
      <Route path="/request-access/verify" element={<Navigate to="/signup" replace />} />
      <Route path="/request-access/set-password" element={<Navigate to="/signup" replace />} />

      {/* Full-screen standalone 3D viewer — chrome-free, opens in new tab */}
      <Route
        path="/projects/:id/viewer"
        element={<Protected><ProjectViewerPage /></Protected>}
      />
      <Route
        path="/projects/:id/pro-viewer"
        element={<Protected><ProViewerPage /></Protected>}
      />
      <Route
        path="/projects/:id/sections/2d"
        element={<Protected><SectionViewer2DPage /></Protected>}
      />
      <Route
        path="/projects/:id/estimation/wizard"
        element={<Protected><EstimationWizardPage /></Protected>}
      />
      <Route
        path="/geostatistics"
        element={<Protected><GeostatisticsPage /></Protected>}
      />
      <Route
        path="/geostatistics/:tool"
        element={<Protected><GeostatisticsPage /></Protected>}
      />
      <Route
        path="/projects/:id/geostatistics"
        element={<Protected><GeostatisticsPage /></Protected>}
      />
      <Route
        path="/projects/:id/geostatistics/:tool"
        element={<Protected><GeostatisticsPage /></Protected>}
      />
      <Route
        path="/enterprise"
        element={<Protected><EnterprisePage /></Protected>}
      />
      <Route
        path="/enterprise/:tool"
        element={<Protected><EnterprisePage /></Protected>}
      />
      <Route
        path="/projects/:id/enterprise"
        element={<Protected><EnterprisePage /></Protected>}
      />
      <Route
        path="/projects/:id/enterprise/:tool"
        element={<Protected><EnterprisePage /></Protected>}
      />

      {/* Sprint 106 — Enterprise Ribbon Workspace (full-screen). */}
      <Route
        path="/workspace"
        element={<Protected><EnterpriseWorkspacePage /></Protected>}
      />
      <Route
        path="/workspace/:id"
        element={<Protected><EnterpriseWorkspacePage /></Protected>}
      />
      <Route
        path="/projects/:id/workspace"
        element={<Protected><EnterpriseWorkspacePage /></Protected>}
      />
      <Route
        path="/workspace/:id/detach"
        element={<Protected><DetachedViewportPage /></Protected>}
      />

      {/* Sprint 106 — public report verification (no auth). */}
      <Route path="/verify/r/:reportId" element={<VerifyReportPage />} />

      {/* Authenticated app — Desktop Enterprise Shell (or Stepper when feature-flagged on) */}
      <Route element={<Protected><ShellSwitcher /></Protected>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/projects/:id/w/:workspace" element={<ProjectDetailPage />} />
        <Route path="/projects/:id/master" element={<ProjectMasterPage />} />
        <Route path="/projects/:id/setup" element={<ProjectMasterPage />} />
        <Route path="/company/knowledge-base" element={<CompanyKnowledgeBasePage />} />
        <Route path="/reports" element={<MyReportsPage />} />
        <Route path="/company/compare" element={<ProjectComparisonPage />} />
        <Route path="/projects/:id/drillhole-import" element={<DrillholeImportPage />} />
        <Route path="/company" element={<CompanySettingsPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/branding" element={<CompanyBrandingPage />} />
        <Route path="/admin/report-templates" element={<ReportTemplatesPage />} />
        {/* /admin/signup-requests retired Feb 2026 — self-serve register replaces the approval gate */}
        <Route path="/admin/signup-requests" element={<Navigate to="/admin/users" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <WorkspaceProvider>
            <ErrorBoundary>
              <Suspense fallback={<RouteSkeleton />}>
                <AppRoutes />
              </Suspense>
            </ErrorBoundary>
            <BackToTop />
            <IdleTimeoutModal />
            <Toaster richColors position="top-right" />
          </WorkspaceProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
